import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

interface User { id: number; guid: string; email: string; role: string; }

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  sessionExpired: boolean;
  register: (token: string, user: any, expires?: string | number) => void;
  login: (token: string, user: any, expires?: string | number) => void;
  logout: (expired?: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Normalizes the backend "expires" value to epoch milliseconds.
// Handles: seconds-until-expiry (e.g. 7200), unix seconds, unix ms, or ISO date string.
const toExpiryMs = (expires: string | number): number | null => {
  const n = Number(expires);
  if (!isNaN(n)) {
    if (n < 1e7) return Date.now() + n * 1000; // relative seconds
    if (n < 1e12) return n * 1000;             // unix seconds
    return n;                                  // unix ms
  }
  const d = Date.parse(String(expires));
  return isNaN(d) ? null : d;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    const exp = Number(sessionStorage.getItem('expiresAt'));
    return !!sessionStorage.getItem('token') && (!exp || exp > Date.now());
  });
  const [user, setUser] = useState<User | null>(() => {
    const stored = sessionStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });
  const [sessionExpired, setSessionExpired] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const logout = useCallback((expired = false) => {
    clearTimeout(timerRef.current);
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('expiresAt');
    setIsAuthenticated(false);
    setUser(null);
    setSessionExpired(expired);
  }, []);

  const scheduleLogout = useCallback(() => {
    clearTimeout(timerRef.current);
    const exp = Number(sessionStorage.getItem('expiresAt'));
    if (!exp) return;
    const remaining = exp - Date.now();
    if (remaining <= 0) logout(true);
    else timerRef.current = setTimeout(() => logout(true), remaining);
  }, [logout]);

  const login = useCallback((token: string, user: any, expires?: string | number) => {
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('user', JSON.stringify(user));
    const expMs = expires != null ? toExpiryMs(expires) : null;
    if (expMs) sessionStorage.setItem('expiresAt', String(expMs));
    setIsAuthenticated(true);
    setUser(user);
    setSessionExpired(false);
    scheduleLogout();
  }, [scheduleLogout]);

  const register = login;

  // Arm the timer on page load/refresh, and re-check when the tab regains focus
  // (timers can be delayed while the device sleeps / tab is in background).
  useEffect(() => {
    scheduleLogout();
    const onVisible = () => { if (document.visibilityState === 'visible') scheduleLogout(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearTimeout(timerRef.current);
    };
  }, [scheduleLogout]);

  // Safety net: any 401 from any axios call logs the user out.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        if (error.response?.status === 401 && sessionStorage.getItem('token')) {
          logout(true);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [logout]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' && e.newValue === null) logout();
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [logout]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, sessionExpired, register, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};